import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useCrmAccess } from "@/hooks/use-crm-access";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Camera, Image, PoundSterling, TrendingUp, Trophy, XCircle, Archive, Handshake } from "lucide-react";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { useDealStages, useDeals } from "@/hooks/use-deals";
import { usePartnerDeals } from "@/hooks/use-partner-deals";
import { useRoleBasedAccess } from "@/hooks/use-role-based-access";
import { DealCalculator } from "@/components/crm/DealCalculator";
import { DealCard } from "@/components/crm/DealCard";
import { useState } from "react";
import { useProposalDiagnostics } from "@/hooks/use-proposal-diagnostics";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const CrmDeals = () => {
  const { data: hasCrmAccess, isLoading } = useCrmAccess();
  const { session } = useAuth();
  const { isPartner, partnerId } = useRoleBasedAccess();

  // Get deals based on user role - use dedicated hook for partners
  const allDeals = useDeals();
  const partnerDeals = usePartnerDeals(partnerId || '');

  // Fetch commercial cameras sold this year from orders (matching Pipeline/KPI logic)
  const currentYear = new Date().getFullYear();
  const { data: commercialCamerasSold } = useQuery({
    queryKey: ["commercial-cameras-sold", currentYear],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          total_cameras,
          sale_id,
          sales!inner(deal_id, crm_deals!inner(status, value))
        `)
        .gte('created_at', `${currentYear}-01-01T00:00:00`)
        .lt('created_at', `${currentYear + 1}-01-01T00:00:00`);
      
      if (error) {
        console.error("❌ [CrmDeals] Error fetching commercial cameras:", error);
        return 0;
      }
      
      // Filter for won deals with value > 0 (commercial only)
      const commercialOrders = (data || []).filter((order: any) => {
        const deal = order.sales?.crm_deals;
        return deal?.status === 'won' && (deal?.value || 0) > 0;
      });
      
      const total = commercialOrders.reduce((sum: number, order: any) => sum + (order.total_cameras || 0), 0);
      console.log(`📊 [CrmDeals] Commercial cameras sold in ${currentYear}: ${total} (from ${commercialOrders.length} orders)`);
      return total;
    }
  });
  
  // Use appropriate data source based on user role
  const dealsData = isPartner && partnerId 
    ? (partnerDeals.data || [])
    : (allDeals.data || []);

  const dealsLoading = isPartner && partnerId ? partnerDeals.isLoading : allDeals.isLoading;
  const { data: stages = [], isLoading: stagesLoading } = useDealStages();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDeal, setSelectedDeal] = useState<any>(null);

  // Add proposal diagnostics
  useProposalDiagnostics();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-slate-600">Loading access...</div>
      </div>
    );
  }

  if (!hasCrmAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  if (dealsLoading || stagesLoading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <AppSidebar />
          <main className="flex-1">
            <div className="p-8">
              <div className="text-slate-600">Loading deals...</div>
            </div>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  // Show error state if there's an error fetching deals
  const dealsError = isPartner && partnerId ? partnerDeals.error : allDeals.error;
  if (dealsError) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <AppSidebar />
          <main className="flex-1">
            <div className="p-8">
              <div className="bg-red-100 border border-red-400 rounded-lg p-4">
                <h3 className="font-semibold text-red-800">Error Loading Deals</h3>
                <p className="text-sm text-red-700 mt-2">There was an error loading the deals data.</p>
                <details className="mt-2">
                  <summary className="text-sm text-red-600 cursor-pointer">View technical details</summary>
                  <pre className="text-xs text-red-600 mt-2 overflow-auto">{JSON.stringify(dealsError, null, 2)}</pre>
                </details>
              </div>
            </div>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  const filteredDeals = dealsData.filter(deal =>
    deal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (deal.contact && deal.contact.first_name && deal.contact.last_name && 
     `${deal.contact.first_name} ${deal.contact.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (deal.organization && deal.organization.name && deal.organization.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Archived deals are those with archived = true (filtered from existing data like lost deals)
  const archivedDeals = filteredDeals.filter(deal => deal.archived === true);

  // Filter stages to only show Opportunity and Proposal in Active Pipeline
  const activeStages = stages.filter(stage => 
    stage.name === 'Opportunity' || stage.name === 'Proposal'
  );

  // Separate deals into different categories with enhanced logging
  const activeDeals = filteredDeals.filter(deal => {
    const isWon = deal.status === 'won';
    const isLost = deal.status === 'lost';
    const isArchived = deal.archived;
    // Include deals with active stages OR deals with null stage_id (temporary fix)
    const hasActiveStage = deal.stage_id && activeStages.some(stage => stage.id === deal.stage_id);
    const hasNullStage = !deal.stage_id; // Include deals with null stage_id temporarily
    
    const isActive = !isWon && !isLost && !isArchived && (hasActiveStage || hasNullStage);
    
    if (deal.id && (isWon || isLost)) {
      console.log(`📊 [CrmDeals] Deal ${deal.id} (${deal.title}) - status: ${deal.status}, stage_id: ${deal.stage_id}, isActive: ${isActive}`);
    }
    
    return isActive;
  });
  
  // Won deals are those with status 'won' regardless of stage_id
  const wonDeals = filteredDeals.filter(deal => {
    const isWon = deal.status === 'won';
    if (isWon) {
      console.log(`🏆 [CrmDeals] Won deal found: ${deal.id} (${deal.title}) - status: ${deal.status}, stage_id: ${deal.stage_id}`);
    }
    return isWon;
  });

  // Current year won deals for secondary text
  const currentYearWonDeals = wonDeals.filter(deal => 
    deal.created_at && new Date(deal.created_at).getFullYear() === currentYear
  );

  
  // Lost deals are those with status 'lost' regardless of stage_id
  const lostDeals = filteredDeals.filter(deal => deal.status === 'lost');

  console.log(`📊 [CrmDeals] Deal counts - Active: ${activeDeals.length}, Won: ${wonDeals.length}, Lost: ${lostDeals.length}, Archived: ${archivedDeals.length}`);

  const getDealsByStage = (stageId: string) => {
    // If this is the Opportunity stage, include any deals with null stage_id as fallback
    const opportunityStage = activeStages.find(stage => stage.name === 'Opportunity');
    if (stageId === opportunityStage?.id) {
      return activeDeals.filter(deal => 
        deal.stage_id === stageId || (!deal.stage_id && deal.status !== 'won' && deal.status !== 'lost' && !deal.archived)
      );
    }
    return activeDeals.filter(deal => deal.stage_id === stageId);
  };

  const getTotalValue = () => {
    // Only count value from active pipeline deals (Opportunity and Proposal stages)
    return activeDeals.reduce((total, deal) => total + (deal.value || 0), 0);
  };

  const getTotalCameras = () => {
    return dealsData.reduce((total, deal) => total + (deal.total_cameras || 0), 0);
  };

  const getTotalImages = () => {
    return dealsData.reduce((total, deal) => total + (deal.total_images || 0), 0);
  };

  const getWonDealsCount = () => {
    return wonDeals.length;
  };

  const getLostDealsCount = () => {
    return lostDeals.length;
  };

  const getArchivedDealsCount = () => {
    return archivedDeals.length;
  };

  const getWonCommercialValue = () => {
    return currentYearWonDeals
      .filter(deal => (deal.value || 0) > 0)
      .reduce((total, deal) => total + (deal.value || 0), 0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader {...PAGE_HEADERS.crmDeals} actions={<DealCalculator />} />
          <div className="px-4 md:px-8 py-6 md:py-8 space-y-8" style={{ paddingTop: "8.5rem" }}>

            {/* Selected Deal Calculator Modal */}
            {selectedDeal && (
              <DealCalculator 
                existingDeal={selectedDeal} 
                onClose={() => setSelectedDeal(null)} 
              />
            )}

            {/* Enhanced Summary Cards with Status Analytics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-white border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Active Opportunities</CardTitle>
                    <TrendingUp className="h-5 w-5 text-teal-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">{activeDeals.length}</div>
                  <p className="text-sm text-slate-600 mt-1">In progress</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Won Cameras ({currentYear})</CardTitle>
                    <Trophy className="h-5 w-5 text-green-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-700">{commercialCamerasSold || 0}</div>
                  <p className="text-sm text-slate-600 mt-1">{currentYearWonDeals.filter(d => (d.value || 0) > 0).length} deals · {formatCurrency(getWonCommercialValue())} won</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Pipeline Value</CardTitle>
                    <PoundSterling className="h-5 w-5 text-teal-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">{formatCurrency(getTotalValue())}</div>
                  <p className="text-sm text-slate-600 mt-1">Total value</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Lost/Archived</CardTitle>
                    <div className="flex gap-1">
                      <XCircle className="h-4 w-4 text-red-600" />
                      <Archive className="h-4 w-4 text-gray-600" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">{getLostDealsCount() + getArchivedDealsCount()}</div>
                  <p className="text-sm text-slate-600 mt-1">{getLostDealsCount()} lost, {getArchivedDealsCount()} archived</p>
                </CardContent>
              </Card>
            </div>

            {/* Search and Filters */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 h-4 w-4" />
                <Input
                  placeholder="Search opportunities, contacts, or organizations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg bg-white"
                />
              </div>
            </div>

            {/* Tabbed Interface with enhanced logging */}
            <Tabs defaultValue="active" className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-white rounded-lg shadow-sm">
                <TabsTrigger value="active" className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Active Pipeline ({activeDeals.length})
                </TabsTrigger>
                <TabsTrigger value="won" className="flex items-center gap-2" onClick={() => {
                  console.log(`🏆 [CrmDeals] Won tab clicked - ${wonDeals.length} won deals available`);
                  wonDeals.forEach(deal => console.log(`  - ${deal.id}: ${deal.title} (status: ${deal.status}, stage_id: ${deal.stage_id})`));
                }}>
                  <Trophy className="h-4 w-4" />
                  Won Deals ({wonDeals.length})
                </TabsTrigger>
                <TabsTrigger value="lost" className="flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Lost Deals ({lostDeals.length})
                </TabsTrigger>
                <TabsTrigger value="archived" className="flex items-center gap-2">
                  <Archive className="h-4 w-4" />
                  Archive ({getArchivedDealsCount()})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="active" className="mt-6">
                {/* Active Pipeline Kanban View - Only Opportunity and Proposal stages */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[600px]">
                  {/* Active Stage Columns - Only Opportunity and Proposal */}
                  {activeStages.map((stage) => {
                    const stageDeals = getDealsByStage(stage.id);
                    const stageValue = stageDeals.reduce((total, deal) => total + (deal.value || 0), 0);

                    return (
                      <div key={stage.id} className="space-y-4">
                        <div className="bg-white rounded-lg p-3 shadow-sm border border-slate-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: stage.color }}
                              />
                              <h3 className="font-semibold text-sm text-slate-800">{stage.name}</h3>
                            </div>
                            <div className="flex flex-col items-end">
                              <Badge variant="secondary" className="text-xs mb-1 bg-teal-100 text-teal-700">
                                {stageDeals.length}
                              </Badge>
                              {stageValue > 0 && (
                                <span className="text-xs text-slate-600 font-medium">
                                  {formatCurrency(stageValue)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3 min-h-[500px] bg-gradient-to-b from-slate-100 to-slate-50 rounded-lg p-3 border border-slate-200">
                          {stageDeals.map((deal) => (
                            <DealCard
                              key={deal.id}
                              deal={deal}
                              stages={activeStages}
                              onClick={() => setSelectedDeal(deal)}
                            />
                          ))}
                          {stageDeals.length === 0 && (
                            <div className="text-center text-slate-500 text-sm py-8 bg-white rounded-lg border-2 border-dashed border-slate-300">
                              No {stage.name.toLowerCase()}s in this stage
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="won" className="mt-6">
                {(() => {
                  console.log(`🏆 [CrmDeals] Rendering Won Deals tab with ${wonDeals.length} deals`);
                  return null;
                })()}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {wonDeals.map((deal) => {
                    console.log(`🏆 [CrmDeals] Rendering won deal card: ${deal.id} - ${deal.title}`);
                    return (
                      <DealCard
                        key={deal.id}
                        deal={deal}
                        stages={stages}
                        onClick={() => setSelectedDeal(deal)}
                      />
                    );
                  })}
                  {wonDeals.length === 0 && (
                    <div className="col-span-full text-center text-slate-500 py-8">
                      <Trophy className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                      <p>No won deals yet</p>
                      {(() => {
                        console.log(`🏆 [CrmDeals] No won deals to display`);
                        return null;
                      })()}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="lost" className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {lostDeals.map((deal) => (
                    <DealCard
                      key={deal.id}
                      deal={deal}
                      stages={stages}
                      onClick={() => setSelectedDeal(deal)}
                    />
                  ))}
                  {lostDeals.length === 0 && (
                    <div className="col-span-full text-center text-slate-500 py-8">
                      <XCircle className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                      <p>No lost deals</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="archived" className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {archivedDeals.map((deal) => (
                    <DealCard
                      key={deal.id}
                      deal={deal}
                      stages={stages}
                      onClick={() => setSelectedDeal(deal)}
                      isArchived={true}
                    />
                  ))}
                  {archivedDeals.length === 0 && (
                    <div className="col-span-full text-center text-slate-500 py-8">
                      <Archive className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                      <p>No archived deals</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            {/* Empty State */}
            {activeStages.length === 0 && dealsData.length === 0 && (
              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-t-xl">
                  <CardTitle className="text-slate-50">Get Started with Opportunities</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-slate-600 mb-4">
                    Create your first opportunity using our interactive calculator. 
                    Build packages with cameras and credits that match your customers' needs.
                  </p>
                  <DealCalculator />
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default CrmDeals;
